/**
 * Shared skipped-day visual treatment for Planning modules.
 *
 * Small surfaces can reuse the hatch style only. Larger cards/popovers render the
 * full overlay with a centered "Day Skipped" label and optional reason editor.
 */
import { useEffect, useState } from "react";

import { updateScope } from "../../../services/api/planning";
import { PLANNING_TOOLTIP_MESSAGES } from "../../../config";
import { usePlanningContext } from "../../../context/domain/planning/PlanningContext";

export const SKIPPED_DAY_HATCH_STYLE = {
  backgroundImage: `repeating-linear-gradient(
    -45deg,
    rgba(151, 192, 255, 0.58) 0 2px,
    transparent 5px 5px
  )`,
};

/**
 * Full-card skipped-day overlay with optional in-place reason editing.
 *
 * @param {Object} props
 * @param {boolean} [props.showLabel=true] Whether to show the centered label.
 * @param {string} [props.className=""] Extra classes for the absolute overlay root.
 * @param {number|string|null} [props.aspectId] WorkScopeAspect id used to persist the skipped reason.
 * @param {string} [props.reason=""] Current skipped reason text.
 * @param {"schedule"|"dispatch"} [props.module="schedule"] Source module for sync logging.
 * @param {(reason: string) => void} [props.onReasonChange] Optimistic parent-state update.
 * @returns {JSX.Element}
 */
export default function SkippedDayOverlay({
  showLabel = true,
  className = "",
  aspectId = null,
  reason = "",
  module = "schedule",
  onReasonChange,
}) {
  const { runPlanningOperation } = usePlanningContext();
  const [localReason, setLocalReason] = useState(reason ?? "");
  const [draftReason, setDraftReason] = useState(reason ?? "");
  const [isEditingReason, setIsEditingReason] = useState(false);

  /** Keep the displayed reason aligned when polling/reloads replace the aspect row. */
  useEffect(() => {
    const nextReason = reason ?? "";
    setLocalReason(nextReason);
    if (!isEditingReason) {
      setDraftReason(nextReason);
    }
  }, [isEditingReason, reason]);

  /**
   * Opens the small reason editor in the center label box.
   *
   * @param {void}
   * @returns {void}
   */
  const openReasonEditor = () => {
    setDraftReason(localReason);
    setIsEditingReason(true);
  };

  /**
   * Optimistically persist the skipped reason when the input loses focus.
   *
   * @param {void}
   * @returns {Promise<void>}
   */
  const commitReason = async () => {
    const nextReason = draftReason.trim();
    setIsEditingReason(false);
    if (nextReason === localReason) {
      return;
    }
    const previousReason = localReason;
    setLocalReason(nextReason);
    onReasonChange?.(nextReason);
    if (aspectId == null) {
      return;
    }
    try {
      await runPlanningOperation({
        module,
        operation: nextReason
          ? "updateScopeSkippedReason"
          : "clearScopeSkippedReason",
        resources: [`${module}.aspects`],
        entityIds: [aspectId],
        apiCall: () =>
          updateScope(Number(aspectId), { skipped_reason: nextReason }, module),
      });
    } catch (err) {
      console.error("Skipped day reason update failed", err);
      setLocalReason(previousReason);
      setDraftReason(previousReason);
      onReasonChange?.(previousReason);
    }
  };

  return (
    <div
      className={`pointer-events-none absolute inset-0 z-[1] flex items-center justify-center bg-gray-200/55 ${className}`.trim()}
      style={{
        ...SKIPPED_DAY_HATCH_STYLE,
        backdropFilter: "grayscale(1) opacity(0.55)",
      }}
      aria-hidden={!showLabel}
    >
      {showLabel ? (
        <div
          className="pointer-events-auto max-w-[85%] cursor-pointer bg-blue-100 p-2 py-0.5 text-center text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-gray-200"
          title={PLANNING_TOOLTIP_MESSAGES.clickToAddReason}
          role={isEditingReason ? undefined : "button"}
          tabIndex={isEditingReason ? undefined : 0}
          onClick={(e) => {
            e.stopPropagation();
            if (!isEditingReason) {
              openReasonEditor();
            }
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (isEditingReason) {
              return;
            }
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openReasonEditor();
            }
          }}
        >
          <div>Day Skipped</div>
          {isEditingReason ? (
            <input
              autoFocus
              className="mt-2 w-full caret-black rounded border border-blue-200 bg-white px-2 py-1 text-sm font-normal text-gray-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="Enter Reason"
              value={draftReason}
              onChange={(e) => setDraftReason(e.target.value)}
              onBlur={commitReason}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                }
                if (e.key === "Escape") {
                  setDraftReason(localReason);
                  setIsEditingReason(false);
                }
              }}
            />
          ) : localReason ? (
            <div className="mt-1 max-w-full truncate text-xs font-medium text-gray-700">
              {localReason}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
